import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { CommentsDataMap, AnnotationStatus } from '@confluence/comments-data';
import { useSessionData } from '@confluence/session-data';
import type { CommentInlineLocation } from '@confluence/comments-panel-queries';
import { fg } from '@confluence/feature-gating';

import { useDanglingComments } from './useDanglingComments';

export const useDanglingOperations = ({
	orderedActiveAnnotationIdList,
	commentsDataMap,
	setCommentsDataMap,
}: {
	orderedActiveAnnotationIdList: AnnotationStatus[];
	commentsDataMap: CommentsDataMap;
	setCommentsDataMap: (commentsDataMap: CommentsDataMap) => void;
}) => {
	const processedMarkerRefsForMark = useRef<Set<string>>(new Set());
	const processedMarkerRefsForUnmark = useRef<Set<string>>(new Set());
	const annotationsSet = useMemo(
		() => new Set(orderedActiveAnnotationIdList.map((item) => item.threadKey)),
		[orderedActiveAnnotationIdList],
	);

	const { cloudId } = useSessionData();

	const { markCommentAsDangling, unmarkCommentAsDangling, commentDanglingRef } =
		useDanglingComments({
			updatedCommentsDataMap: commentsDataMap,
			setCommentsDataMap,
		});

	const handleMarkCommentAsDangling = useCallback(
		(commentThreadId: string, markerRef: string) => {
			commentDanglingRef.current = markerRef;
			return markCommentAsDangling({
				variables: {
					input: {
						id: commentThreadId,
					},
					cloudId,
				},
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[markCommentAsDangling, cloudId],
	);

	const handleUnmarkCommentAsDangling = useCallback(
		(commentThreadId: string, markerRef: string) => {
			commentDanglingRef.current = markerRef;
			return unmarkCommentAsDangling({
				variables: {
					input: {
						id: commentThreadId,
					},
					cloudId,
				},
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[unmarkCommentAsDangling, cloudId],
	);

	useEffect(() => {
		if (fg('confluence_frontend_dangling_comments')) {
			const processDanglingComments = async () => {
				for (const threadKey in commentsDataMap.inline) {
					const commentThread = commentsDataMap.inline[threadKey];
					const isDanglingComment = (commentThread?.location as CommentInlineLocation)
						?.inlineResolveProperties?.isDangling;

					if (commentThread?.isOpen) {
						// if the comment is already dangling, check if it's in the annotations set and unmark it if it is
						if (isDanglingComment) {
							const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;
							if (
								markerRef &&
								annotationsSet.has(markerRef) &&
								!processedMarkerRefsForUnmark.current.has(markerRef)
							) {
								const result = await handleUnmarkCommentAsDangling(commentThread.id, markerRef);
								if (result?.data?.confluence_unmarkCommentAsDangling?.success) {
									processedMarkerRefsForUnmark.current.add(markerRef);
								}
							}
							continue;
						}

						// check for real time dangling comments
						const markerRef = (commentThread?.location as CommentInlineLocation)?.inlineMarkerRef;
						// comment is dangling if it has a marker ref and is not in the annotations set
						// deleted and resolved comments are not in commentsDataMap so we know it's only dangling scenario
						if (
							markerRef &&
							!annotationsSet.has(markerRef) &&
							!processedMarkerRefsForMark.current.has(markerRef)
						) {
							const result = await handleMarkCommentAsDangling(commentThread.id, markerRef);
							if (result?.data?.confluence_markCommentAsDangling?.success) {
								processedMarkerRefsForMark.current.add(markerRef);
							}
						}
					}
				}
				// reset the sets for the next run
				processedMarkerRefsForMark.current = new Set();
				processedMarkerRefsForUnmark.current = new Set();
			};

			void processDanglingComments();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [handleMarkCommentAsDangling, handleUnmarkCommentAsDangling, annotationsSet]);

	return {
		annotationsSet,
	};
};
